import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  Space,
  Row,
  Col,
  Tag,
  Typography,
  Breadcrumb,
} from 'antd';
import { SearchOutlined, ReloadOutlined, HomeOutlined, SettingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { getActivityLogs } from '../../../../api/activityLog';
import { ActivityLog, ActivityLogFilter, ActivityType } from '../../../../types/activityLog';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const StyledCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
`;

const ActionTag = styled(Tag)`
  font-weight: 500;
`;

const ActivityLogs: React.FC = () => {
  const { t } = useTranslation(['activityLog', 'common', 'menu']);
  const [loading, setLoading] = useState<boolean>(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [filter, setFilter] = useState<{
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 10,
  });
  const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null] | null>(
    null,
  );
  const [searchName, setSearchName] = useState<string>('');

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const fromDate = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
      const toDate = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;

      const response = await getActivityLogs(
        filter.page,
        filter.limit,
        searchName || undefined,
        fromDate,
        toDate,
      );

      console.log('API Response:', response);

      if (response.result && Array.isArray(response.result)) {
        setActivityLogs(response.result);
        setTotal(response.total || 0);
      } else if (response.data && response.data.result && Array.isArray(response.data.result)) {
        // Fallback nếu response có cấu trúc khác
        setActivityLogs(response.data.result);
        setTotal(response.data.total || 0);
      } else {
        console.error('Unexpected API response structure:', response);
        setActivityLogs([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page, filter.limit]);

  const handleSearch = () => {
    setFilter({ ...filter, page: 1 });
    fetchActivityLogs();
  };

  const handleReset = () => {
    setFilter({
      page: 1,
      limit: 10,
    });
    setDateRange(null);
    setSearchName('');
  };

  const handleTableChange = (pagination: any) => {
    setFilter({
      ...filter,
      page: pagination.current,
      limit: pagination.pageSize,
    });
  };

  const getActionTagColor = (action: string) => {
    switch (action) {
      case 'LOGIN':
        return 'green';
      case 'LOGOUT':
        return 'orange';
      case 'CREATE':
        return 'blue';
      case 'UPDATE':
        return 'purple';
      case 'DELETE':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: t('activityLog:time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
      width: 180,
    },
    {
      title: t('activityLog:user'),
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text: string | null, record: ActivityLog) => (
        <div>
          <div>{text || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.email || ''}</div>
        </div>
      ),
      width: 200,
    },
    {
      title: t('activityLog:action'),
      dataIndex: 'action',
      key: 'action',
      render: (text: string) => <ActionTag color={getActionTagColor(text)}>{text}</ActionTag>,
      width: 120,
    },
    {
      title: t('activityLog:description'),
      dataIndex: 'description',
      key: 'description',
      width: 250,
    },
    {
      title: t('activityLog:module'),
      dataIndex: 'module',
      key: 'module',
      width: 120,
    },
    {
      title: t('activityLog:ip_address'),
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 150,
    },
    {
      title: t('activityLog:device_info'),
      key: 'deviceInfo',
      render: (record: ActivityLog) => (
        <div>
          <div>{record.device}</div>
          <div>{record.os}</div>
          <div>{record.browser}</div>
        </div>
      ),
      width: 200,
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ marginBottom: '16px' }}></Breadcrumb>
      <Title level={4}>{t('activityLog:activity_logs')}</Title>

      <StyledCard>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder={t('activityLog:search_by_name')}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) =>
                setDateRange(dates as [moment.Moment | null, moment.Moment | null])
              }
              format="DD/MM/YYYY"
              placeholder={[t('activityLog:from_date'), t('activityLog:to_date')]}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={4}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                {t('activityLog:search')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                {t('activityLog:reset')}
              </Button>
            </Space>
          </Col>
        </Row>
      </StyledCard>

      <Table
        columns={columns}
        dataSource={activityLogs}
        rowKey="id"
        loading={loading}
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => t('activityLog:total_items', { total }),
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default ActivityLogs;
